import React, { useState, useMemo, useEffect, useCallback } from "react";
import { Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import {
  Box,
  Typography,
  FormControl,
  Select,
  MenuItem,
  TextField,
  Autocomplete,
  Tooltip,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { ExpandMore } from "@mui/icons-material";
import { CustomNoRowsOverlay } from "../../../components/common/OverlayGrid";
import { useTheme } from "../../../ThemeContext";

/**
 * Screen 2b: Contract — Review & Correct Matches Modal
 * Built to specifications from Mockup Screen 2b.
 * Blocks allocation until ambiguous or missing resource matches are confirmed.
 */
const defaultResources = [
  "Rajkumar",
  "Ramkumar M",
  "Surya",
  "Indhumathi R",
  "Nirmalraj S",
];

const ReviewMatchesModal = ({
  show,
  onHide,
  invoice,
  projectUsers = [],
  onConfirmAllocate,
  onBackToContract,
}) => {
  const { isDarkMode } = useTheme();
  const isAllocated =
    invoice?.status?.toLowerCase() === "allocated" ||
    invoice?.rawItem?.status?.toLowerCase() === "allocated";

  const [matchLines, setMatchLines] = useState([
    {
      id: 1,
      desc: "Contract staffing services (India)",
      hint: "Resource: Rajkumar · Aug 26 · 168 hrs",
      amount: "80,000.00",
      resource: "Rajkumar",
      type: "Billable",
      matchTag: "High confidence",
      tagVariant: "success",
      bgStyle: "",
    },
    {
      id: 2,
      desc: "Staffing — R. Kumar, Aug",
      hint: "Ambiguous: matches both Rajkumar and Ramkumar M",
      amount: "78,750.00",
      resource: "Rajkumar",
      type: "Billable",
      matchTag: "Low confidence ⚠",
      tagVariant: "warning",
      bgStyle: "#fffbeb",
    },
    {
      id: 3,
      desc: "Professional charges — Aug",
      hint: "No resource name found in the description",
      amount: "45,000.00",
      resource: "Surya",
      type: "Billable",
      matchTag: "No match",
      tagVariant: "danger",
      bgStyle: "#fef2f2",
    },
    {
      id: 4,
      desc: "Technical consulting & architecture — Aug",
      hint: "Resource: Indhumathi R · Aug 26 · 140 hrs",
      amount: "65,000.00",
      resource: "Indhumathi R",
      type: "Billable",
      matchTag: "High confidence",
      tagVariant: "success",
      bgStyle: "",
    },
    {
      id: 5,
      desc: "Client onsite travel & subsistence reimbursement",
      hint: "Travel allowance verified with receipt attached",
      amount: "15,000.00",
      resource: "Nirmalraj S",
      type: "Reimbursement",
      matchTag: "High confidence",
      tagVariant: "success",
      bgStyle: "",
    },
  ]);

  useEffect(() => {
    if (invoice?.lines && Array.isArray(invoice.lines) && invoice.lines.length > 0) {
      const isAlloc =
        invoice?.status?.toLowerCase() === "allocated" ||
        invoice?.rawItem?.status?.toLowerCase() === "allocated";

      const users = invoice.projectUsers || projectUsers || [];
      const mapped = invoice.lines.map((item, idx) => {
        const billedVal = Number(item.earning ?? item.billed_amount ?? 0);
        const amountFmt = billedVal.toLocaleString("en-IN", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });

        const matchedUser = users.find(
          (u) => Number(u.user_id) === Number(item.user_id)
        );
        const resName = matchedUser?.name || item.resource || item.user_name || "Unassigned";

        const rawHrs = item.working_hours ?? item.extracted_hours ?? 0;
        const decimalHrs = typeof rawHrs === "number" ? rawHrs : parseFloat(rawHrs) || 0;
        const formattedHrs = decimalHrs % 1 === 0 ? decimalHrs : Number(decimalHrs.toFixed(2));

        const hintStr = item.billing_type?.toLowerCase() === "reimbursement"
          ? "Type: reimbursement"
          : matchedUser
            ? `Resource: ${resName} · Worked ${formattedHrs} hrs`
            : "No resource assigned";

        return {
          id: item.line_item_id || item.id || idx + 1,
          desc: item.line_item_name || item.desc || `Line ${idx + 1} · Contract staffing services`,
          hint: hintStr,
          amount: amountFmt,
          resource: resName,
          type: item.billing_type || "Billable",
          matchTag: isAlloc ? "Allocated ✓" : (matchedUser ? "High confidence" : "No match"),
          tagVariant: isAlloc ? "success" : (matchedUser ? "success" : "danger"),
          bgStyle: isAlloc ? "" : (matchedUser ? "" : "#fef2f2"),
        };
      });
      setMatchLines(mapped);
    }
  }, [invoice?.lines, invoice?.projectUsers, invoice?.status, invoice?.rawItem?.status, projectUsers]);

  const resourceOptions = useMemo(() => {
    const userMap = new Map();
    const users = invoice?.availableUsers || invoice?.projectUsers || projectUsers || [];
    if (Array.isArray(users)) {
      users.forEach((u) => {
        const name = typeof u === "string" ? u : u?.name || u?.username;
        const id = typeof u === "object" ? u?.user_id || u?.id : null;
        if (name && !userMap.has(name)) {
          userMap.set(name, { user_id: id, name });
        }
      });
    }
    matchLines.forEach((m) => {
      if (m.resource && m.resource !== "Unassigned" && !userMap.has(m.resource)) {
        userMap.set(m.resource, { user_id: m.user_id || null, name: m.resource });
      }
    });
    if (userMap.size === 0) {
      defaultResources.forEach((name, i) => userMap.set(name, { user_id: i + 1, name }));
    }
    return Array.from(userMap.values());
  }, [invoice?.availableUsers, invoice?.projectUsers, projectUsers, matchLines]);

  const totalAmount = useMemo(() => {
    const sum = matchLines.reduce((acc, row) => {
      const num = parseFloat(String(row.amount).replace(/,/g, "")) || 0;
      return acc + num;
    }, 0);
    return sum.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }, [matchLines]);

  const isScrollable = matchLines.length > 3;

  const matchCounts = useMemo(() => {
    let lowConfidence = 0;
    let noMatch = 0;
    matchLines.forEach((m) => {
      if (m.matchTag === "Low confidence ⚠" || m.tagVariant === "warning") lowConfidence++;
      if (m.matchTag === "No match" || m.tagVariant === "danger") noMatch++;
    });
    return { lowConfidence, noMatch, hasIssues: lowConfidence > 0 || noMatch > 0 };
  }, [matchLines]);

  const isAllocateDisabled = useMemo(() => {
    if (!matchLines || matchLines.length === 0) return true;
    return matchLines.some(
      (m) =>
        !m.resource ||
        m.resource === "Unassigned" ||
        m.resource === "Select & search..." ||
        m.matchTag === "No match" ||
        m.tagVariant === "danger"
    );
  }, [matchLines]);

  const handleResourceChange = (id, newResource) => {
    setMatchLines((prev) =>
      prev.map((item) => {
        if (item.id === id) {
          const hasRes = Boolean(newResource && newResource !== "Unassigned" && newResource !== "Select & search...");
          return {
            ...item,
            resource: newResource || "",
            matchTag: hasRes ? "Confirmed ✓" : "No match",
            tagVariant: hasRes ? "success" : "danger",
            bgStyle: hasRes ? "#f0fdf4" : "#fef2f2",
          };
        }
        return item;
      })
    );
    if (newResource) {
      toast.success("Match updated and verified!");
    }
  };

  const handleTypeChange = (id, newType) => {
    setMatchLines((prev) =>
      prev.map((item) => (item.id === id ? { ...item, type: newType } : item))
    );
  };

  const handleAddLine = () => {
    const nextNum = matchLines.length + 1;
    const newLine = {
      id: Date.now(),
      desc: `Additional staffing line ${nextNum}`,
      hint: "Manually added resource line",
      amount: "0.00",
      resource: resourceOptions[0] || "Rajkumar",
      type: "Billable",
      matchTag: "Confirmed ✓",
      tagVariant: "success",
      bgStyle: "#f0fdf4",
    };
    setMatchLines((prev) => [...prev, newLine]);
    toast.info(`Resource line ${nextNum} added!`);
  };

  const handleConfirm = () => {
        if (onConfirmAllocate) {
      onConfirmAllocate();
        }
        onHide();
  };

  const renderStatusBadge = useCallback(
    (row) => {
      let bg, color, border;
      if (row.matchTag === "Confirmed ✓" || row.tagVariant === "success") {
        bg = isDarkMode ? "rgba(16, 185, 129, 0.15)" : "#ecfdf5";
        color = isDarkMode ? "#34d399" : "#059669";
        border = isDarkMode ? "1px solid rgba(16, 185, 129, 0.3)" : "1px solid #a7f3d0";
      } else if (row.tagVariant === "warning") {
        bg = isDarkMode ? "rgba(245, 158, 11, 0.15)" : "#fffbeb";
        color = isDarkMode ? "#fbbf24" : "#d97706";
        border = isDarkMode ? "1px solid rgba(245, 158, 11, 0.3)" : "1px solid #fde68a";
      } else {
        bg = isDarkMode ? "rgba(239, 68, 68, 0.15)" : "#fef2f2";
        color = isDarkMode ? "#f87171" : "#dc2626";
        border = isDarkMode ? "1px solid rgba(239, 68, 68, 0.3)" : "1px solid #fecaca";
      }
      return (
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            height: "24px",
            padding: "0 8px",
            borderRadius: "4px",
            fontSize: "12px",
            fontWeight: 600,
            backgroundColor: bg,
            color,
            border,
          }}
        >
          {row.matchTag}
        </span>
      );
    },
    [isDarkMode]
  );

  const columns = useMemo(
    () => [
      {
        field: "desc",
        headerName: "Line description",
        flex: 1,
        minWidth: 260,
        sortable: false,
        renderCell: (params) => (
          <Box sx={{ py: 1, display: "flex", flexDirection: "column", justifyContent: "center", width: "100%" }}>
            <Typography sx={{ fontWeight: 600, fontSize: "13px", color: isDarkMode ? "#f8fafc" : "#1e293b", lineHeight: 1.3 }}>
              {params.row.desc}
            </Typography>
            <Typography
              sx={{
                fontSize: "11px",
                color: params.row.tagVariant === "warning" ? (isDarkMode ? "#fbbf24" : "#b45309") : (isDarkMode ? "#94a3b8" : "#64748b"),
                mt: 0.3,
                fontWeight: params.row.tagVariant === "warning" ? 500 : 400,
              }}
            >
              {params.row.hint}
            </Typography>
          </Box>
        ),
      },
      {
        field: "amount",
        headerName: "Amount",
        width: 150,
        headerAlign: "right",
        align: "right",
        sortable: false,
        renderCell: (params) => (
          <span
            style={{
              fontSize: "13px",
              fontWeight: 500,
              color: isDarkMode ? "#f8fafc" : "#334155",
              fontVariantNumeric: "tabular-nums",
              paddingRight: "6px",
            }}
          >
            {params.value}
          </span>
        ),
      },
      {
        field: "resource",
        headerName: "Resource",
        width: 220,
        headerAlign: "center",
        align: "center",
        sortable: false,
        renderCell: (params) => {
          if (isAllocated) {
            return (
              <Box sx={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <span style={{ fontSize: "13px", color: isDarkMode ? "#f8fafc" : "#1e293b", fontWeight: 500 }}>
                  {params.row.resource || "—"}
                </span>
              </Box>
            );
          }

          const currentVal =
            resourceOptions.find(
              (u) =>
                (params.row.user_id && Number(u.user_id) === Number(params.row.user_id)) ||
                u.name === params.row.resource
            ) || (params.row.resource && params.row.resource !== "Unassigned" ? { name: params.row.resource } : null);

          return (
            <Box
              sx={{
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Autocomplete
                size="small"
                options={resourceOptions}
                getOptionLabel={(option) => (typeof option === "string" ? option : option.name || "")}
                isOptionEqualToValue={(option, value) => {
                  if (!value) return false;
                  if (option.user_id && value.user_id) return Number(option.user_id) === Number(value.user_id);
                  return (option.name || option) === (value.name || value);
                }}
                getOptionDisabled={(option) => {
                  const optName = typeof option === "string" ? option : option.name;
                  const currentName = params.row.resource;
                  if (!optName || optName === "Unassigned" || optName === "Select & search...") return false;
                  if (optName === currentName) return false;
                  return matchLines.some((m) => m.id !== params.row.id && m.resource === optName);
                }}
                value={currentVal}
                onChange={(event, newValue) => {
                  handleResourceChange(params.row.id, newValue ? (newValue.name || newValue) : "");
                }}
                renderOption={(props, option) => {
                  const optName = typeof option === "string" ? option : option.name;
                  const currentName = params.row.resource;
                  const isAlreadySelected = Boolean(
                    optName &&
                    optName !== "Unassigned" &&
                    optName !== "Select & search..." &&
                    optName !== currentName &&
                    matchLines.some((m) => m.id !== params.row.id && m.resource === optName)
                  );

                  return (
                    <li
                      {...props}
                      key={option.user_id || optName}
                      style={{
                        fontSize: "14px",
                        minHeight: "36px",
                        padding: "6px 12px",
                        fontFamily: "inherit",
                        cursor: isAlreadySelected ? "not-allowed" : "pointer",
                        opacity: isAlreadySelected ? 0.55 : 1,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        backgroundColor: isAlreadySelected
                          ? (isDarkMode ? "rgba(255, 255, 255, 0.04)" : "#f1f5f9")
                          : undefined,
                      }}
                    >
                      <Tooltip
                        title={isAlreadySelected ? "This resource is already selected in another line" : ""}
                        placement="right"
                        arrow
                        disableHoverListener={!isAlreadySelected}
                      >
                        <span
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            width: "100%",
                            pointerEvents: "auto",
                          }}
                        >
                          <span style={{ fontWeight: isAlreadySelected ? 400 : 500 }}>{optName}</span>
                          {isAlreadySelected && (
                            <span
                              style={{
                                fontSize: "11px",
                                fontStyle: "italic",
                                color: isDarkMode ? "#94a3b8" : "#64748b",
                                marginLeft: "8px",
                              }}
                            >
                              (Already selected)
                            </span>
                          )}
                        </span>
                      </Tooltip>
                    </li>
                  );
                }}
                renderInput={(inputParams) => (
                  <TextField
                    {...inputParams}
                    placeholder="Select & search..."
                    size="small"
                    onKeyDown={(e) => e.stopPropagation()}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        fontSize: "14px",
                        height: "32px !important",
                        minHeight: "32px !important",
                        boxSizing: "border-box",
                        padding: "0 8px 0 10px !important",
                        backgroundColor: isDarkMode ? "#1e293b" : "#fff",
                        color: isDarkMode ? "#f2f1f1" : "#1e293b",
                        "& fieldset": {
                          borderColor: isDarkMode ? "#444" : "#d1d5db",
                        },
                        "&:hover fieldset": {
                          borderColor: isDarkMode ? "#666" : "#94a3b8",
                        },
                      },
                      "& .MuiOutlinedInput-input": {
                        padding: "4px 0 !important",
                        fontSize: "14px",
                        color: isDarkMode ? "#f2f1f1" : "#1e293b",
                      },
                    }}
                  />
                )}
                sx={{
                  width: "100%",
                  maxWidth: 190,
                  mt: "26px",
                }}
              />
            </Box>
          );
        },
      },
      {
        field: "type",
        headerName: "Type",
        width: 170,
        headerAlign: "center",
        align: "center",
        sortable: false,
        renderCell: (params) => (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <FormControl size="small" sx={{ width: "100%", maxWidth: 140 }}>
              <Select
                value={params.row.type || "Billable"}
                onChange={(e) => handleTypeChange(params.row.id, e.target.value)}
                disabled={isAllocated}
                size="small"
              IconComponent={isAllocated ? () => null : ExpandMore}
              MenuProps={{
                sx: { zIndex: 1600 },
                PaperProps: {
                  sx: {
                    zIndex: 1600,
                    maxHeight: 280,
                    backgroundColor: isDarkMode ? "#222" : "#fff",
                    color: isDarkMode ? "#f1f5f9" : "inherit",
                    border: isDarkMode ? "1px solid #444" : "none",
                  },
                },
              }}
              sx={{
                fontSize: "14px",
                height: "32px",
                backgroundColor: isAllocated ? (isDarkMode ? "#222" : "#f8fafc") : (isDarkMode ? "#1e1e1e" : "#ffffff"),
                cursor: isAllocated ? "default" : "pointer",
                "& .MuiSelect-select": {
                  fontSize: "14px",
                  paddingTop: "4px !important",
                  paddingBottom: "4px !important",
                  paddingLeft: "10px !important",
                  paddingRight: isAllocated ? "10px !important" : "24px !important",
                  display: "flex",
                  alignItems: "center",
                  color: isDarkMode ? "#f8fafc" : "#1e293b",
                  WebkitTextFillColor: isDarkMode ? "#f8fafc !important" : "#1e293b !important",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: isDarkMode ? "#444" : "#d1d5db",
                },
                "&.Mui-disabled": {
                  backgroundColor: isDarkMode ? "#222" : "#f8fafc",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: isDarkMode ? "#383838" : "#e2e8f0",
                  },
                },
              }}
            >
              <MenuItem
                value="Billable"
                sx={{
                  color: isDarkMode ? "#f1f5f9" : "inherit",
                  "&:hover": { backgroundColor: isDarkMode ? "#333" : "#f1f5f9" },
                }}
              >
                Billable
              </MenuItem>
              <MenuItem
                value="Reimbursement"
                sx={{
                  color: isDarkMode ? "#f1f5f9" : "inherit",
                  "&:hover": { backgroundColor: isDarkMode ? "#333" : "#f1f5f9" },
                }}
              >
                Reimbursement
              </MenuItem>
            </Select>
          </FormControl>
        </Box>
      ),
    },
      {
        field: "matchStatus",
        headerName: "Match Status",
        width: 180,
        headerAlign: "center",
        align: "center",
        sortable: false,
        renderCell: (params) => (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              height: "100%",
            }}
          >
            {renderStatusBadge(params.row)}
          </Box>
        ),
      },
    ],
    [resourceOptions, isAllocated, isDarkMode, renderStatusBadge, matchLines]
  );

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      size="xl"
      contentClassName={isDarkMode ? "bg-dark text-white border-secondary" : ""}
    >
      <Modal.Header closeButton className={isDarkMode ? "bg-dark text-white border-secondary" : ""}>
        <div className="d-flex align-items-center gap-2">
          <Modal.Title className="fs-6 fw-bold m-0">
            Review matches · {invoice?.invoice || invoice?.invoice_number || invoice?.id || "TEST-000001"}
          </Modal.Title>
          <span className={`badge ${isDarkMode ? "bg-secondary text-white" : "bg-light text-dark"} border`}>
            {invoice?.source || "TEST"}
          </span>
          <span className="badge-model-contract">
            Contract
          </span>
          {isAllocated && (
            <span className="badge bg-success-subtle text-success border">
              Allocated
            </span>
          )}
        </div>
      </Modal.Header>

      <Modal.Body className={`p-4 ${isDarkMode ? "bg-dark text-white" : ""}`}>
        {/* Warning / Status Banner */}
        {isAllocated ? (
          <div
            className="alert alert-success py-2 px-3 small border-0 mb-3"
            style={{
              backgroundColor: isDarkMode ? "rgba(16, 185, 129, 0.12)" : "#ecfdf5",
              color: isDarkMode ? "#34d399" : "#065f46",
              borderLeft: "4px solid #10b981",
              border: isDarkMode ? "1px solid rgba(16, 185, 129, 0.25)" : "none",
              borderRadius: "6px",
            }}
          >
            <strong>Allocated Matches (Read-Only):</strong> All resource matches and line items for this contract invoice have already been confirmed and allocated.
          </div>
        ) : matchCounts.hasIssues ? (
          <div
            className="alert alert-warning py-2 px-3 small border-0 mb-3"
            style={{
              backgroundColor: isDarkMode ? "rgba(245, 158, 11, 0.12)" : "#fffbeb",
              color: isDarkMode ? "#fbbf24" : "#92400e",
              borderLeft: "4px solid #f59e0b",
              border: isDarkMode ? "1px solid rgba(245, 158, 11, 0.25)" : "none",
              borderRadius: "6px",
            }}
          >
            <strong>Check every match before allocating.</strong>{" "}
            {matchCounts.noMatch > 0 ? `${matchCounts.noMatch} line could not be matched` : ""}
            {matchCounts.noMatch > 0 && matchCounts.lowConfidence > 0 ? " and " : ""}
            {matchCounts.lowConfidence > 0 ? `${matchCounts.lowConfidence} was matched with low confidence` : ""}.
            {" "}Nothing is allocated until all lines are confirmed.
          </div>
        ) : (
          <div
            className="alert alert-success py-2 px-3 small border-0 mb-3"
            style={{
              backgroundColor: isDarkMode ? "rgba(16, 185, 129, 0.12)" : "#ecfdf5",
              color: isDarkMode ? "#34d399" : "#065f46",
              borderLeft: "4px solid #10b981",
              border: isDarkMode ? "1px solid rgba(16, 185, 129, 0.25)" : "none",
              borderRadius: "6px",
            }}
          >
            <strong>All matches verified:</strong> All resource lines are assigned with high confidence. Ready to confirm and allocate.
          </div>
        )}

        {/* Matches Correction Table in MUI DataGrid */}
        <Box
          sx={{
            width: "100%",
            mb: 3,
            backgroundColor: isDarkMode ? "#1e1e1e" : "#fff",
            height: isScrollable ? 286 : "auto",
            overflow: "hidden",
            border: isDarkMode ? "1px solid #333" : "1px solid #e0e0e0",
            borderRadius: "6px",
          }}
        >
          <DataGrid
            rows={matchLines}
            columns={columns}
            autoHeight={!isScrollable}
            rowHeight={60}
            hideFooterPagination
            disableRowSelectionOnClick
            getRowId={(row) => row.id}
            getRowClassName={(params) => {
              if (params.row.matchTag === "Confirmed ✓") return "re-grid-row-confirmed";
              if (params.row.tagVariant === "warning") return "re-grid-row-warning";
              if (params.row.tagVariant === "danger") return "re-grid-row-danger";
              return "";
            }}
            slots={{
              footer: () => (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    borderTop: isDarkMode ? "1px solid #333" : "1px solid #e0e0e0",
                    backgroundColor: isDarkMode ? "#1e1e1e" : "#f8fafc",
                    minHeight: "48px",
                    px: 0,
                    width: "100%",
                    color: isDarkMode ? "#f8fafc" : "#1e293b",
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 260, pl: "10px", fontWeight: 700 }}>Total</Box>
                  <Box
                    sx={{
                      width: 150,
                      minWidth: 150,
                      maxWidth: 150,
                      flexShrink: 0,
                      textAlign: "right",
                      pr: "16px",
                      fontWeight: 700,
                      fontSize: "13px",
                      color: isDarkMode ? "#4ade80" : "#16a34a",
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {totalAmount}
                  </Box>
                  <Box sx={{ width: 220, minWidth: 220, maxWidth: 220, flexShrink: 0 }} />
                  <Box sx={{ width: 170, minWidth: 170, maxWidth: 170, flexShrink: 0 }} />
                  <Box sx={{ width: 180, minWidth: 180, maxWidth: 180, flexShrink: 0 }} />
                  {isScrollable && <Box sx={{ width: "4px", flexShrink: 0 }} />}
                </Box>
              ),
              noRowsOverlay: CustomNoRowsOverlay,
            }}
            sx={{
              height: isScrollable ? "100%" : "auto",
              border: "none !important",
              "& .MuiDataGrid-main": {
                overflow: "hidden",
              },
              "& .MuiDataGrid-scrollbar, & .MuiDataGrid-scrollbar--vertical, & .MuiDataGrid-scrollbar--horizontal": {
                display: "none !important",
                width: "0 !important",
                height: "0 !important",
                visibility: "hidden !important",
                opacity: 0,
                pointerEvents: "none",
              },
              "& .MuiDataGrid-scrollbarContent": {
                display: "none !important",
              },
              "& .MuiDataGrid-virtualScroller": {
                overflowY: "auto !important",
                scrollbarWidth: "thin !important",
                scrollbarColor: "transparent transparent !important",
                transition: "scrollbar-color 0.2s ease-in-out",
              },
              "&:hover .MuiDataGrid-virtualScroller": {
                scrollbarColor: "#cbd5e1 transparent !important",
              },
              "& .MuiDataGrid-virtualScroller::-webkit-scrollbar": {
                width: "4px !important",
                height: "4px !important",
              },
              "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-button": {
                display: "none !important",
                width: "0 !important",
                height: "0 !important",
              },
              "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-corner": {
                display: "none !important",
              },
              "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-track": {
                backgroundColor: "transparent !important",
              },
              "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb": {
                backgroundColor: "transparent !important",
                borderRadius: "4px !important",
                transition: "background-color 0.2s ease-in-out",
              },
              "&:hover .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb": {
                backgroundColor: "#cbd5e1 !important",
              },
              "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb:hover": {
                backgroundColor: "#94a3b8 !important",
              },
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: isDarkMode ? "#1e1e1e" : "#fff",
                borderBottom: isDarkMode ? "1px solid #333" : "1px solid #e0e0e0",
                fontSize: "13px",
                fontWeight: 600,
                color: isDarkMode ? "#9ca3af" : "#6b7280",
              },
              "& .MuiDataGrid-columnHeaderTitle": {
                fontWeight: 600,
              },
              "& .MuiDataGrid-cell": {
                fontSize: "13px",
                borderBottom: isDarkMode ? "1px solid #2a2a2a" : "1px solid #f0f0f0",
                display: "flex",
                alignItems: "center",
                color: isDarkMode ? "#f1f5f9" : "inherit",
              },
              "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
                outline: "none !important",
              },
              "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within": {
                outline: "none !important",
              },
              "& .MuiDataGrid-row:hover": {
                backgroundColor: isDarkMode ? "rgba(255, 255, 255, 0.04)" : "#fafafa",
              },
              "& .re-grid-row-warning": {
                backgroundColor: isDarkMode ? "rgba(245, 158, 11, 0.08) !important" : "#fffbeb !important",
                "&:hover": {
                  backgroundColor: isDarkMode ? "rgba(245, 158, 11, 0.14) !important" : "#fef3c7 !important",
                },
              },
              "& .re-grid-row-danger": {
                backgroundColor: isDarkMode ? "rgba(239, 68, 68, 0.08) !important" : "#fef2f2 !important",
                "&:hover": {
                  backgroundColor: isDarkMode ? "rgba(239, 68, 68, 0.14) !important" : "#fee2e2 !important",
                },
              },
              "& .re-grid-row-confirmed": {
                backgroundColor: isDarkMode ? "rgba(16, 185, 129, 0.08) !important" : "#f0fdf4 !important",
                "&:hover": {
                  backgroundColor: isDarkMode ? "rgba(16, 185, 129, 0.14) !important" : "#dcfce7 !important",
                },
              },
              "& .MuiDataGrid-footerContainer": {
                borderTop: isDarkMode ? "1px solid #333" : "1px solid #e0e0e0",
                minHeight: "48px",
                padding: "0 !important",
              },
            }}
          />
        </Box>

        {/* Ambiguous Match Explanation Banner - only when not allocated */}
        {!isAllocated && (
          <div
            className="alert alert-danger py-2 px-3 small border-0 mb-3"
            style={{
              backgroundColor: isDarkMode ? "rgba(239, 68, 68, 0.12)" : "#fef2f2",
              color: isDarkMode ? "#f87171" : "#991b1b",
              borderLeft: "4px solid #ef4444",
              border: isDarkMode ? "1px solid rgba(239, 68, 68, 0.25)" : "none",
              borderRadius: "6px",
            }}
          >
            <strong>The middle row is the case that matters:</strong> "R. Kumar" is one character
            away from two real people on our payroll (Rajkumar and Ramkumar M). Left alone, an
            automatic match could allocate money against the wrong person. Low-confidence matches are
            therefore verified here before final allocation.
          </div>
        )}

        <div className="text-muted small d-flex align-items-center gap-1" style={{ fontSize: "11.5px" }}>
          <span>💡</span>
          <span>
            <strong>Pattern learning:</strong> Once a description pattern is confirmed against a
            resource for a client, the same pattern parses automatically next month — so the
            correction is made once, not every month.
          </span>
        </div>
      </Modal.Body>

      <Modal.Footer className={`${isDarkMode ? "bg-dark text-white border-secondary" : "bg-light"} d-flex align-items-center justify-content-between p-3`}>
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary"
          style={{
            borderRadius: "4px",
            fontSize: "13px",
            fontWeight: 500,
            height: "36px",
            padding: "0 16px",
          }}
          onClick={onBackToContract || onHide}
        >
          Back to Contract
        </button>
        {!isAllocated && (
          <div className="d-flex align-items-center gap-2">
            <button
              type="button"
              className="re-btn-sync"
              onClick={handleAddLine}
            >
              + Add resource line
            </button>
            <button
              type="button"
              className="re-btn-manual"
              disabled={isAllocateDisabled}
              onClick={handleConfirm}
              style={
                isAllocateDisabled
                  ? { opacity: 0.5, cursor: "not-allowed" }
                  : undefined
              }
            >
              Confirm &amp; Allocate
            </button>
          </div>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default ReviewMatchesModal;

